import { useState, useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface DropdownChoice {
  value: string;
  label: string;
  color?: string;
}

interface DropdownCellProps {
  value: string | string[] | null;
  choices: DropdownChoice[];
  onChange: (value: string | string[] | null) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  allowMultiple?: boolean;
  showBadge?: boolean;
  className?: string;
}

export function DropdownCell({
  value,
  choices,
  onChange,
  isEditing,
  onStartEdit,
  onEndEdit,
  allowMultiple = false,
  showBadge = true,
  className,
}: DropdownCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Normalize value to array for multi-select handling
  const selectedValues = Array.isArray(value)
    ? value
    : value
      ? [value]
      : [];

  useEffect(() => {
    if (isEditing) {
      setIsOpen(true);
      setSearchTerm('');
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setIsOpen(false);
    }
  }, [isEditing]);

  const handleSelect = useCallback(
    (choiceValue: string) => {
      if (allowMultiple) {
        const newValues = selectedValues.includes(choiceValue)
          ? selectedValues.filter((v) => v !== choiceValue)
          : [...selectedValues, choiceValue];
        flushSync(() => { onChange(newValues.length > 0 ? newValues : null); });
      } else {
        flushSync(() => { onChange(choiceValue); });
        setIsOpen(false);
        onEndEdit();
      }
    },
    [allowMultiple, selectedValues, onChange, onEndEdit]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        onEndEdit();
      }
    },
    [onEndEdit]
  );

  const filteredChoices = choices.filter((c) =>
    c.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getChoiceLabel = (val: string) =>
    choices.find((c) => c.value === val)?.label ?? val;

  const getChoiceColor = (val: string) =>
    choices.find((c) => c.value === val)?.color;

  const renderBadge = (val: string) => {
    const color = getChoiceColor(val);
    const label = getChoiceLabel(val);

    if (showBadge && color) {
      return (
        <span
          key={val}
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mr-1"
          style={{
            backgroundColor: `${color}20`,
            color: color,
            border: `1px solid ${color}40`,
          }}
        >
          {label}
        </span>
      );
    }

    return (
      <span key={val} className="mr-1">
        {label}
      </span>
    );
  };

  const displayContent = () => {
    if (selectedValues.length === 0) {
      return <span className="text-gray-400">Select...</span>;
    }

    if (allowMultiple && selectedValues.length > 2) {
      return (
        <>
          {selectedValues.slice(0, 2).map(renderBadge)}
          <span className="text-gray-500 text-xs">
            +{selectedValues.length - 2} more
          </span>
        </>
      );
    }

    return selectedValues.map(renderBadge);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            'w-full h-full px-2 py-1 text-sm flex items-center justify-between cursor-pointer',
            'hover:bg-gray-50',
            isEditing && 'bg-white ring-2 ring-blue-500',
            className
          )}
          onClick={onStartEdit}
        >
          <div className="flex-1 truncate flex items-center flex-wrap gap-0.5">
            {displayContent()}
          </div>
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <div className="p-2 border-b">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-2 py-1 text-sm border rounded outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="max-h-60 overflow-auto">
          {filteredChoices.length === 0 ? (
            <div className="p-2 text-sm text-gray-500 text-center">
              No options found
            </div>
          ) : (
            filteredChoices.map((choice) => (
              <div
                key={choice.value}
                className={cn(
                  'flex items-center px-2 py-2 cursor-pointer hover:bg-gray-100',
                  selectedValues.includes(choice.value) && 'bg-blue-50'
                )}
                onClick={() => handleSelect(choice.value)}
              >
                <div
                  className={cn(
                    'w-4 h-4 mr-2 border rounded flex items-center justify-center',
                    selectedValues.includes(choice.value)
                      ? 'bg-blue-500 border-blue-500'
                      : 'border-gray-300'
                  )}
                >
                  {selectedValues.includes(choice.value) && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </div>
                {choice.color && showBadge ? (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: `${choice.color}20`,
                      color: choice.color,
                      border: `1px solid ${choice.color}40`,
                    }}
                  >
                    {choice.label}
                  </span>
                ) : (
                  <span className="text-sm">{choice.label}</span>
                )}
              </div>
            ))
          )}
        </div>
        {allowMultiple && selectedValues.length > 0 && (
          <div className="p-2 border-t flex justify-between">
            <button
              className="text-xs text-gray-500 hover:text-gray-700"
              onClick={() => onChange(null)}
            >
              Clear all
            </button>
            <button
              className="text-xs text-blue-500 hover:text-blue-700"
              onClick={() => {
                setIsOpen(false);
                onEndEdit();
              }}
            >
              Done
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default DropdownCell;
